import '../Pages.scss';
import React, { FunctionComponent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import ErrorBoundary from '@justgiving/error-boundary';

import {
  TeamAdminTabs,
  EventDetails,
  PageSection,
  Story,
  AboutCampaignAndCharitySection,
} from '../../components';

import TeamCTA from '../../components/TeamCTA/TeamCTA';
import FeatureImage from '../../components/FeatureImage/FeatureImage';
import Totals from '../../components/Totals/Totals';
import TotaliserWrapper from '../../components/TotaliserWrapper/TotaliserWrapper';
import AboutTeam from '../../components/AboutTeam/AboutTeam';
import MembersList from '../../components/MembersList/MembersList';
import MembersMini from '../../components/MembersMini/MembersMini';
import IsPageOwner from '../../components/IsPageOwner/IsPageOwner';
import JoinTeam from '../../components/JoinTeam/JoinTeam';
import AboutCauseMini from '../../components/AboutCauseMini/AboutCauseMini';
import DonationSummary from '../../components/DonationSummary/DonationSummary';
import SupportersList from '../../components/SupportersList/SupportersList';
import SocialButtonsListWrapper from '../../components/SocialButtons/SocialButtonsListWrapper';
import IsQRCode from '../../components/IsQRCode/IsQRCode';
import TeamsHelmet from '../../components/Helmet/TeamsHelmet';
import Page from '../../components/Page/Page';
import TeamLayout from '../../components/Layout/TeamLayout';
import { filterByTypename } from '../../utils/graphql';
import { firstArrayItem } from '../../utils/firstArrayItem';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import QRModal from '../../components/QRModal/QRModal';
import { Banner } from '../../components/Banner/Banner';

const TeamsPage: FunctionComponent = () => {
  const basePageData = useBasePageQuery();
  const data = basePageData.data?.page;
  const { t } = useTranslation('common', { useSuspense: false });

  const title = data?.title;
  const beneficiaries = data?.relationships?.beneficiaries;
  const charities = filterByTypename(beneficiaries?.nodes ?? [], 'Charity');

  const firstCharity = firstArrayItem(charities);

  const shareSubject = t('team.shareTitle', '{{title}} are fundraising!', {
    replace: { title },
  });
  const shareMessage = t(
    'team.shareMessage',
    'Please support {{title}} who are fundraising for {{charityName}} on JustGiving.',
    {
      replace: {
        title,
        charityName: firstCharity?.name,
      },
      count: beneficiaries?.totalCount ?? 0,
    },
  );
  const membersListRef = React.createRef<HTMLElement>();

  return (
    <Page>
      <IsQRCode>
        <QRModal ownerName={title} />
      </IsQRCode>
      <TeamsHelmet />
      <Banner />
      <JoinTeam />
      <IsPageOwner>
        <TeamAdminTabs />
      </IsPageOwner>
      <TeamLayout
        slots={{
          featureImage: <FeatureImage />,
          membersMini: <MembersMini />,
          title: <>{title}</>,
          totals: (
            <>
              <TotaliserWrapper />
              <Totals />
            </>
          ),
          callsToAction: <TeamCTA membersListRef={membersListRef} />,
          titleSupplementary: (
            <>
              <p className="jg-text--light jg-pages-teams-charitysummary jg-space-mbn">
                <Trans
                  i18nKey="team.fundraisingFor"
                  count={(beneficiaries && beneficiaries.totalCount) || 0}
                >
                  Team fundraising for{' '}
                  <a
                    href={`${firstCharity && firstCharity.profilePageUrl}`}
                    className="jg-text--regular"
                  >
                    {{ charityName: firstCharity && firstCharity.name }}
                  </a>
                </Trans>
              </p>
              <ErrorBoundary fallbackContent={<></>}>
                <EventDetails />
              </ErrorBoundary>
            </>
          ),
          causeMini: <AboutCauseMini />,
          story: (
            <PageSection
              title={t('story.title')}
              className="jg-pages-teams-story"
            >
              <Story>
                <p>{t('team.defaultStory.part1')}</p>
                <p>{t('team.defaultStory.part2')}</p>
                <p>{t('team.defaultStory.part3')}</p>
              </Story>

              <h2 className="jg-text--center jg-h3">
                <Trans i18nKey="story.shareTitle">Share this story</Trans>
              </h2>
              <SocialButtonsListWrapper
                subject={shareSubject}
                message={shareMessage}
              />
            </PageSection>
          ),
          main: <MembersList listRef={membersListRef} />,
          aside: (
            <SupportersList
              noSupportersMessage={t(
                'team.noSupportersMessage',
                'Try making a donation to a team member.',
              )}
            />
          ),
          aboutTeam: (
            <PageSection
              title={t('page.aboutTheTeam')}
              hasBorder
              titleClass="jg-h5 jg-space-mbmd"
            >
              <AboutTeam />
            </PageSection>
          ),
          contentFooterPrimary: (
            <AboutCampaignAndCharitySection beneficiariesLimit={24} />
          ),
          contentFooterSecondary: (
            <DonationSummary fieldsToShow={['onlineAmount', 'offlineAmount']} />
          ),
        }}
      />
      <script
        src="//rum-static.pingdom.net/pa-5d2710d114cfaa00080000fc.js"
        async
      ></script>
    </Page>
  );
};

export default TeamsPage;
